import React, { useState, useMemo, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Footer from '../components/Footer';

// ─── FAQ data ─────────────────────────────────────────────────────────────────
const FAQS = [
  {
    category: 'Getting Started',
    icon: '🚀',
    items: [
      {
        q: 'What is LiteRent?',
        a: "LiteRent is a platform that lets you move into your desired home by paying just 30% upfront. The remaining 70% is spread over 11 monthly instalments, making housing accessible without needing a full year's rent at once.",
      },
      {
        q: 'Who can use LiteRent?',
        a: 'Any Ibadan resident aged 18 or older with a verifiable source of income, a valid means of identification, and at least two guarantors. Both employed and self-employed individuals are welcome to apply.',
      },
      {
        q: 'How do I create an account?',
        a: 'Click "Sign Up" on the homepage, enter your name, email and phone number, then verify your account via OTP. Once verified, complete your tenant profile — personal details, employment info, bank details and guarantor contacts — before you can browse and apply.',
      },
      {
        q: 'Is there a fee to join LiteRent?',
        a: 'Creating an account is completely free. A one-time verification fee (₦10,000 for properties under ₦1M/year, or ₦20,000 for ₦1M/year and above) is only charged when you submit an application for a specific property.',
      },
      {
        q: 'What age do I need to be to use LiteRent?',
        a: 'You must be at least 18 years old to create an account and apply for a property on LiteRent. You will be required to verify your age through your NIN or other government-issued ID.',
      },
      {
        q: 'Do I need a smartphone to use LiteRent?',
        a: 'LiteRent is fully accessible on any modern smartphone browser — no app download required. You can also use it on a laptop or desktop computer. We recommend using a recent version of Chrome, Safari, or Firefox for the best experience.',
      },
      {
        q: 'Can I use LiteRent if I live outside Ibadan?',
        a: 'Currently, LiteRent operates exclusively within Ibadan and requires Nigerian identity documents. We plan to expand to other Nigerian cities in the future. Subscribe to our newsletter to be notified.',
      },
      {
        q: 'What cities does LiteRent operate in?',
        a: 'LiteRent currently serves Ibadan. We hope to expand rapidly — send us a message to let us know you want us in your city.',
      },
    ],
  },

  // ── VERIFICATION ──────────────────────────────────────────────────────────
  {
    category: 'Verification',
    icon: '✅',
    items: [
      {
        q: 'What is the verification process?',
        a: "LiteRent's verification process has two parts. First, you submit your documents online — your ID, proof of income, and guarantor details. Second, a member of our team conducts a physical verification visit to your home or place of work to confirm that the information you have provided is accurate and that you are genuinely employed or actively earning. Both steps must be completed before your application can be approved.",
      },
      {
        q: 'Why does LiteRent do a physical verification visit?',
        a: 'The physical visit allows us to confirm that your identity, residence, and employment or income details are genuine. This protects every tenant and property owner on our platform. It ensures that the person applying is real, that the income they declared is consistent with what we observe, and that the guarantors provided are legitimate. The visit is brief and professional — most applicants complete it without any difficulty.',
      },
      {
        q: 'What happens during the physical verification visit?',
        a: 'A LiteRent representative will visit the address you have provided — typically your workplace, business premises, or place of residence. The visit usually takes between 20 and 45 minutes. The representative will confirm your identity against your submitted ID, verify your employment or business activity, and may speak briefly with a colleague, supervisor, or household member as a reference. You will be informed of the visit date and time in advance so you can prepare.',
      },
      {
        q: 'How much is the verification fee?',
        a: 'The verification fee is ₦10,000 for annual rents below ₦1,000,000, and ₦20,000 for annual rents of ₦1,000,000 and above. This is a one-time fee paid when you submit your application for a specific property. It covers the cost of the physical visit, document review, identity checks, and income assessment.',
      },
      {
        q: 'When do I pay the verification fee?',
        a: 'The verification fee is paid at the point of submitting your application for a property. You will be prompted to pay it before your application enters the review queue. The fee must be paid before our team can begin processing your application.',
      },
      {
        q: 'Is the verification fee refundable?',
        a: 'No. The verification fee is non-refundable once paid, even if your application is ultimately rejected. This is because the fee covers work that begins immediately — the physical visit is scheduled, your documents are reviewed, and your income is assessed. All of this requires time and resources regardless of the outcome.',
      },
      {
        q: 'What documents do I need to provide for verification?',
        a: "You will need to submit: a valid government-issued photo ID (National Identity Card, NIN slip, Voter's Card, or International Passport); proof of income (recent payslip, employment letter, or bank statements for the last three months); and contact details for at least two guarantors including their own valid IDs. Self-employed applicants should also provide business registration documents and three to six months of bank statements showing consistent income.",
      },
      {
        q: 'What if I am self-employed — how does verification work for me?',
        a: 'Self-employed applicants are very welcome to apply. During the physical visit, our team may visit your business premises or confirm your business activity at your registered address. You should have your CAC registration documents, recent bank statements (3–6 months), and any evidence of business activity such as contracts, invoices, or client correspondence ready. We assess each self-employed applicant on the strength and consistency of their financial history.',
      },
      {
        q: 'Can I reschedule the physical verification visit?',
        a: 'Yes. If the scheduled date is not convenient, contact our support team at least 48 hours in advance and we will arrange an alternative. Repeated rescheduling without a genuine reason may delay your application and, in some cases, result in it being withdrawn.',
      },
      {
        q: 'What happens if I am not available during the verification visit?',
        a: 'If you are unavailable on the scheduled date and did not reschedule in advance, our team will attempt the visit once more. If the second attempt is also unsuccessful, your application will be placed on hold. You will need to contact support to arrange a new date, and your application timeline will restart from that point.',
      },
      {
        q: 'Will my employer or landlord be contacted during verification?',
        a: 'Our team may contact your employer or a supervisor to briefly confirm your employment status and duration. We do this professionally and discreetly — we will not disclose the details of your rental application. For self-employed applicants, we may contact a known client or business associate as a reference. Your guarantors will also be contacted to confirm their willingness to serve in that capacity.',
      },
      {
        q: 'How long does the verification process take?',
        a: 'Most verifications are completed within 3 to 7 business days of your application being submitted and the fee being paid. During peak periods this may extend to 14 business days. You will be notified by email and in-app notification at each stage — when your visit is scheduled, when it has been completed, and when a decision has been made.',
      },
      {
        q: 'What can cause a verification to fail?',
        a: 'Common reasons include: the information submitted not matching what is found during the physical visit; inconsistent or insufficient income evidence; an employer or business that cannot be confirmed; guarantors who do not meet eligibility requirements or who decline to participate; or submission of false, expired, or altered documents. If your verification fails, you will be notified and given the opportunity to address the issue before reapplying.',
      },
      {
        q: 'Can I reapply if my verification fails?',
        a: 'Yes. You may reapply after 30 days, or earlier if the specific issue that caused the failure has been resolved — for example, if a guarantor was replaced or missing documents have been obtained. Contact our support team for guidance specific to your situation. A new verification fee will apply to any new application.',
      },
      {
        q: 'Is my verification information kept confidential?',
        a: 'Yes. All information collected during verification is handled in strict confidence in accordance with the Nigeria Data Protection Act 2023 (NDPA) and our Privacy Policy. Your personal and financial data is never shared with third parties except where required by law. Verification findings are shared only with LiteRent team members directly involved in your application.',
      },
      {
        q: 'Do I need to be verified again when I renew my lease?',
        a: 'No full re-verification is required for a straightforward renewal in the same property. However, if your income or employment situation has changed significantly, or if you are applying for a higher-value property, a partial or full re-verification may be requested. A renewal fee of ₦7,000 applies to all renewals.',
      },
      {
        q: 'What if I submit false documents for verification?',
        a: 'Submitting false, forged, or misleading documents constitutes fraud under the Nigerian Criminal Code Act (Cap. C38, LFN 2004). LiteRent will immediately reject the application, permanently ban the account, and report the matter to the appropriate law enforcement authorities. We take document fraud extremely seriously as it harms the integrity of the platform and the trust placed in it by property owners and other tenants.',
      },
    ],
  },

  // ── APPLICATIONS ──────────────────────────────────────────────────────────
  {
    category: 'Applications',
    icon: '📋',
    items: [
      {
        q: 'How do I apply for a house?',
        a: 'Complete your tenant profile in the dashboard first — all fields marked with * are required. Once your profile is complete, browse available properties, select a house, and click "Apply for Rent-Later Plan." Fill in the short application form and submit. Track your status under "My Applications" in the dashboard.',
      },
      {
        q: 'What documents do I need to apply?',
        a: "You will need a valid government-issued ID (NIN slip, passport or driver's licence), your proof of income (recent payslip or 3 months' bank statement), and contact details for at least two guarantors including their own ID.",
      },
      {
        q: 'How long does the review process take?',
        a: 'Most applications are reviewed within 3–7 business days. You will be notified by email and in-app notification once a decision is made. During busy periods this may extend to 14 business days.',
      },
      {
        q: 'Can I apply for more than one property at a time?',
        a: 'Yes — you may submit applications for multiple properties simultaneously. However, once one is approved, all other pending applications are automatically withdrawn, as only one active lease is allowed at a time.',
      },
      {
        q: 'What happens after my application is approved?',
        a: 'Our team will contact you to schedule lease signing, confirm your upfront payment and arrange your move-in date. The full process from approval to move-in typically takes 3–14 working days.',
      },
      {
        q: 'Why was my application rejected?',
        a: 'Common reasons include insufficient income relative to the rent, failed identity or employment verification, guarantor issues, or incomplete documentation. You may reapply after 30 days or once the issues are resolved. Contact support for specific feedback on your case.',
      },
      {
        q: 'What income level do I need to qualify?',
        a: 'As a general guideline, your monthly net income should be at least 2.63× the monthly instalment amount. For example, if your monthly instalment is ₦150,000, your required monthly income would be ₦394,500. This is assessed alongside other factors including your employment history and guarantor strength.',
      },
      {
        q: 'Can a self-employed person apply?',
        a: 'Yes. Self-employed applicants should provide 3–6 months of bank statements showing consistent income, a CAC business registration certificate (if applicable), and any other proof of income such as contracts or invoices. Your application will be assessed on the strength of your financial history.',
      },
      {
        q: 'How many guarantors do I need?',
        a: 'A minimum of two guarantors is required, but providing more significantly strengthens your application. Guarantors must have a verifiable income, a valid ID, and must not currently be in debt themselves.',
      },
      {
        q: 'Can my guarantor be a family member?',
        a: 'Yes, family members are accepted as guarantors. However, they must meet the same financial requirements — a verifiable income, a valid government-issued ID, and a clean financial standing. A spouse living in the same household may not qualify as the sole guarantor.',
      },
      {
        q: 'Can I withdraw my application?',
        a: 'Yes. You can withdraw a pending application at any time by going to "My Applications" in your dashboard and selecting "Withdraw." Once withdrawn, the verification fee for that application is non-refundable. Approved applications cannot be withdrawn — please contact support immediately if your situation changes.',
      },
      {
        q: 'How do I check my application status?',
        a: 'Log in to your dashboard and navigate to "My Applications." Each application shows a real-time status: Submitted, Under Review, Additional Info Required, Approved, or Rejected. You will also receive push notifications and email updates at every stage.',
      },
    ],
  },

  // ── PAYMENTS ──────────────────────────────────────────────────────────────
  {
    category: 'Payments',
    icon: '💳',
    items: [
      {
        q: 'How does the LiteRent payment plan work?',
        a: 'On move-in day you pay 30% of the annual rent plus the verification fee. The remaining 70% is repaid in 11 monthly instalments with 4.8% monthly interest applied to the outstanding balance. Your exact monthly amount is displayed clearly before you submit an application.',
      },
      {
        q: 'When are my monthly payments due?',
        a: 'Payments are due on the same date each month as your move-in date. If you moved in on the 10th, all future payments are due on the 10th. You will receive a reminder notification 5 days before each due date.',
      },
      {
        q: 'What payment methods does LiteRent accept?',
        a: 'LiteRent accepts bank transfers, debit card payments, and USSD transfers — all processed through a secure, CBN-licensed payment gateway. Cash payments are not accepted under any circumstances.',
      },
      {
        q: 'What happens if I miss a payment?',
        a: 'A 5-day grace period applies after each due date. After that, a late fee may apply. Continued non-payment may lead to lease termination in accordance with your signed agreement. Please contact us immediately if you anticipate difficulty.',
      },
      {
        q: 'Can I pay off my balance early?',
        a: 'Yes — early settlement is actively encouraged. Use your Payments dashboard to request a settlement quote. There is no prepayment penalty; you only owe the remaining principal plus interest accrued up to the settlement date.',
      },
      {
        q: 'Is automatic payment available?',
        a: "Yes. Enable auto-pay from your Payments dashboard to automatically debit your registered account on each due date. You can disable or update this at any time with at least 3 business days' notice before the next payment.",
      },
      {
        q: 'What is the interest rate on LiteRent?',
        a: 'LiteRent charges 4.8% monthly interest on the outstanding 70% balance during the first year. For subsequent years (renewals), the rate reduces to 4.4% per month on the full annual rent, spread over 12 months. All rates are displayed clearly in our Rent Calculator before you apply.',
      },
      {
        q: 'Is the verification fee refundable?',
        a: 'The verification fee is non-refundable once paid, even if your application is ultimately rejected. This covers the cost of the physical visit, identity verification, and income assessment that begins immediately after you apply.',
      },
      {
        q: 'What is the renewal fee?',
        a: 'When renewing your lease for a subsequent year, a fixed renewal fee of ₦7,000 applies. This is separate from your monthly instalments and is due at the start of the renewed lease period.',
      },
      {
        q: 'Can I change my payment date?',
        a: 'Payment dates are tied to your move-in date and generally cannot be changed. If you have a genuine hardship reason, contact our support team at least 10 business days before your due date and we will review your request on a case-by-case basis.',
      },
      {
        q: 'What happens if my auto-debit fails?',
        a: 'If your auto-debit fails, we will retry after 24 hours and notify you by SMS and email. If payment is still not made within the 5-day grace period, a late fee will be applied. We recommend keeping sufficient funds in your registered account at all times.',
      },
      {
        q: 'Do I get a payment receipt?',
        a: 'Yes. Every payment generates an automatic receipt emailed to your registered address and also stored permanently under "Payments" in your dashboard.',
      },
    ],
  },

  // ── PROPERTIES ────────────────────────────────────────────────────────────
  {
    category: 'Properties',
    icon: '🏠',
    items: [
      {
        q: 'How does LiteRent verify properties?',
        a: 'Every listing passes a multi-step check: our field team physically inspects the property, verifies ownership documents (Certificate of Occupancy or equivalent), and checks for legal encumbrances. Only properties that pass all checks receive the "Verified" badge on the listing.',
      },
      {
        q: 'I found a house offline — can LiteRent finance it?',
        a: 'Yes! Use the "Upload a House" feature in your dashboard to submit the property details. Our team will contact the landlord, run the standard verification checks, and onboard the property if it qualifies. You will be updated at every stage.',
      },
      {
        q: 'Are the houses furnished or unfurnished?',
        a: 'Both options are available depending on the listing. Each property page clearly states whether it is furnished, semi-furnished, or unfurnished. You can use the filters on the Browse page to narrow results by furnishing status.',
      },
      {
        q: 'What types of properties does LiteRent list?',
        a: 'LiteRent currently lists self-contained flats, one-bedroom to five-bedroom apartments, terraced duplexes, semi-detached and detached houses. We focus on residential properties — commercial properties may be supported but terms and conditions will apply.',
      },
      {
        q: 'Can I negotiate the rent on a LiteRent property?',
        a: "The listed rent reflects an agreed price between the landlord and LiteRent. Direct negotiation with landlords is not permitted through the platform. If you feel a price is significantly above market, contact our support team and we will review it.",
      },
      {
        q: 'What if the property has issues after I move in?',
        a: 'Property conditions are primarily managed by landlords. For serious or unresolved issues, LiteRent may step in to mediate on your behalf. Report any concerns through the "Support" tab in your dashboard.',
      },
      {
        q: 'Can I bring pets to a LiteRent property?',
        a: 'Pet policy varies by landlord. Please inform us during the verification period so we can confirm whether the property owner permits pets before your application is finalised.',
      },
      {
        q: 'How often are new properties added?',
        a: 'New verified properties are added every week. Turn on notifications in your dashboard under "Settings" to receive instant alerts when new listings matching your saved preferences are published.',
      },
    ],
  },

  // ── ACCOUNT & SECURITY ────────────────────────────────────────────────────
  {
    category: 'Account & Security',
    icon: '🔐',
    items: [
      {
        q: 'How do I change my password?',
        a: 'In your dashboard, go to Profile & Settings, click the "Settings" tab, then "Change Password." A 6-digit one-time code will be sent to your registered phone number. After verifying the code, you can set your new password.',
      },
      {
        q: 'How is my personal data protected?',
        a: 'LiteRent is fully compliant with the Nigeria Data Protection Act 2023 (NDPA) and the Nigeria Data Protection Regulation (NDPR). All data is encrypted at rest and in transit. We never sell your personal data to third parties. To request a copy or deletion of your data, email support@literent.com.ng.',
      },
      {
        q: 'I forgot my password — how do I reset it?',
        a: "On the login page, click \"Forgot Password?\" and enter your registered phone number. A 6-digit OTP will be sent to you, valid for 10 minutes. If it doesn't arrive, check you entered the correct number or contact support.",
      },
      {
        q: 'Can I update my profile after submitting an application?',
        a: 'Basic contact details can be updated at any time from your profile. Changes to financial or identity information while an application is under review must be made through our support team, as they may affect your credit assessment.',
      },
      {
        q: 'What happens if my phone number changes?',
        a: 'Contact our support team immediately at support@literent.com.ng with your old and new phone numbers plus a copy of your ID. Because your phone number is tied to OTP verification, this change must be manually verified by our team before it takes effect.',
      },
      {
        q: 'Can I have two accounts on LiteRent?',
        a: 'No. Each person is permitted one account. Creating duplicate accounts to circumvent application limits or rejection decisions is a violation of our Terms of Service and may result in all accounts being permanently suspended.',
      },
      {
        q: 'How do I delete my account?',
        a: 'To request account deletion, email support@literent.com.ng from your registered email address. We will process your request within 14 business days. Note that accounts with an active lease or outstanding balance cannot be deleted until all obligations are settled.',
      },
      {
        q: 'What should I do if I suspect unauthorised access to my account?',
        a: 'Change your password immediately from your dashboard under Profile & Settings. Then contact our support team via WhatsApp or email to flag the incident. We can temporarily freeze your account while we investigate if needed.',
      },
    ],
  },

  // ── LEASE & LEGAL ─────────────────────────────────────────────────────────
  {
    category: 'Lease & Legal',
    icon: '📄',
    items: [
      {
        q: 'Who signs the lease agreement?',
        a: 'The lease is a three-party agreement signed by you (the tenant), the landlord, and LiteRent as the intermediary. LiteRent guarantees the full annual rent to the landlord upfront on your behalf; you repay LiteRent through your 11 monthly instalments.',
      },
      {
        q: 'What happens when my lease is about to expire?',
        a: 'You will receive a renewal reminder at least 60 days before your lease expiry date. You may choose to renew under the same or updated terms, or give notice to vacate. If vacating, LiteRent will inspect the property for damage beyond normal wear and tear.',
      },
      {
        q: 'Can I end my lease before it expires?',
        a: "Early termination is possible subject to your lease terms — typically requiring 30–60 days' written notice and settlement of the remaining outstanding balance. Contact our support team as early as possible and we will work with you to find a fair resolution.",
      },
      {
        q: 'What if the landlord sells the property during my lease?',
        a: 'Your tenancy rights are fully protected for the entire duration of your signed lease. A property sale does not invalidate your tenancy — the new owner acquires the property subject to the existing lease agreement. LiteRent will notify you promptly if this situation arises.',
      },
      {
        q: 'Can LiteRent evict me?',
        a: 'Eviction is an absolute last resort and only occurs after repeated non-payment and failure to engage with our support team on a resolution plan. We are required by Nigerian tenancy law to follow proper legal notice procedures before any eviction action, and we will always attempt to resolve issues amicably first.',
      },
      {
        q: 'Am I responsible for property maintenance?',
        a: "Tenants are responsible for general upkeep and minor repairs (replacing light bulbs, cleaning, etc.). Major structural repairs or appliance failures are the landlord's responsibility. Your lease agreement will clearly define which maintenance obligations fall to which party.",
      },
      {
        q: 'Can I sublet the property to someone else?',
        a: 'Subletting is strictly prohibited without written permission from both the landlord and LiteRent. Unauthorised subletting is a breach of your lease agreement and grounds for immediate termination. If your circumstances change and you need to vacate early, contact support to explore your options.',
      },
      {
        q: 'What happens to my lease if LiteRent shuts down?',
        a: "Your signed lease agreement is a legal contract between you and the landlord. LiteRent's role as intermediary does not affect the validity of your tenancy rights. In the unlikely event of LiteRent ceasing operations, our wind-down plan ensures all active tenants are protected and transitioned safely.",
      },
      {
        q: 'Can I renew my lease on a different property?',
        a: 'Yes. At the end of your current lease you are free to apply for a completely different property on LiteRent rather than renewing your existing one. Your previous payment history on LiteRent will strengthen your new application significantly.',
      },
    ],
  },

  // ── SUPPORT ───────────────────────────────────────────────────────────────
  {
    category: 'Support',
    icon: '💬',
    items: [
      {
        q: 'How do I contact LiteRent support?',
        a: 'You can reach us via WhatsApp chat, email at support@literent.com.ng, or by submitting a ticket from your dashboard under the "Support" tab. Our team is available Monday to Saturday, 9 am – 5 pm WAT and typically responds within 2 hours.',
      },
      {
        q: 'What counts as an emergency situation?',
        a: 'Emergencies include total loss of electricity or running water, serious structural damage (roof leak, broken exterior door), or any situation that makes the property unsafe to occupy. For these, use the 24-hour emergency line listed in your Welcome Pack sent on move-in day.',
      },
      {
        q: 'How do I submit a formal complaint?',
        a: 'Submit a ticket from your dashboard under "Support" and select "Formal Complaint" as the category. All formal complaints are escalated to a senior team member within 24 hours and receive a written response within 5 business days. If you are unsatisfied with the resolution, you may escalate to our CEO at ceo@literent.com.ng.',
      },
      {
        q: 'Can I give feedback about a property or landlord?',
        a: 'Yes. After move-in, you can leave a verified review of the property through your "My Rentals" dashboard. Honest, factual feedback helps us maintain quality standards across our listings. Abusive or defamatory reviews will be removed.',
      },
      {
        q: 'Is there a referral program?',
        a: 'Yes! Refer a friend who successfully signs a lease on LiteRent and you will receive an 11.11% credit on your interest towards your next payment. Share your unique referral code from your dashboard. There is no limit to how many referrals you can make.',
      },
      {
        q: 'Does LiteRent have a mobile app?',
        a: 'LiteRent currently operates as a fully responsive web application accessible from any mobile browser — no download required. A dedicated iOS and Android app is in development and expected to launch in the second half of 2025. Watch our social media for the announcement.',
      },
    ],
  },
];

// ─── Accordion item ───────────────────────────────────────────────────────────
function AccordionItem({ question, answer, highlight }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-2xl border-2 transition-all duration-200 overflow-hidden bg-white ${
      open ? 'border-[#a5c250] shadow-md' : 'border-[#e8f2d1] hover:border-[#afc09e] hover:shadow-sm'
    }`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-5 sm:px-6 py-4 sm:py-5 flex items-start justify-between gap-4 focus:outline-none"
        style={{ minHeight: 56 }}
      >
        <span className="font-semibold text-[#1a1a1a] leading-snug text-sm sm:text-base">{question}</span>
        <span className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0 mt-0.5 transition-all duration-300 ${
          open ? 'bg-[#a5c250] rotate-45' : 'bg-[#294c79]'
        }`}>+</span>
      </button>
      {open && (
        <div className="px-5 sm:px-6 pb-5 sm:pb-6">
          <div className="h-px bg-[#e8f2d1] mb-4" />
          <p className="text-[#1a1a1a]/70 leading-relaxed text-sm sm:text-base">{answer}</p>
        </div>
      )}
    </div>
  );
}

// ─── Main FAQ page ────────────────────────────────────────────────────────────
export default function FAQPage() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { isLoggedIn, logout, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const adminToken = localStorage.getItem('adminAuthToken');
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isAdminLoggedIn = !!adminToken && adminUser.role;

  const getDashboardLink = () => isAdminLoggedIn ? '/admin' : '/dashboard';
  const getDashboardLabel = () => {
    if (isAdminLoggedIn) return 'Admin Panel';
    return user?.name ? `Hi, ${user.name.split(' ')[0]}` : 'Dashboard';
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    logout();
    navigate('/', { replace: true });
    setTimeout(() => window.location.reload(), 100);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return FAQS
      .filter((cat) => !activeCategory || cat.category === activeCategory)
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          ({ q: question, a }) => !q || question.toLowerCase().includes(q) || a.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [search, activeCategory]);

  const totalResults = filtered.reduce((n, c) => n + c.items.length, 0);
  const totalQuestions = FAQS.reduce((n, c) => n + c.items.length, 0);

  const scrollToVerification = () => {
    setActiveCategory('Verification');
    setTimeout(() => {
      document.getElementById('faq-body')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  return (
    <div className="min-h-screen bg-[#fdfdfd]" style={{ overflowX: 'hidden' }}>

      {/* ── NAVBAR ── */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#afc09e] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex-shrink-0">
              <img
                src="/Logo.jpg"
                alt="LiteRent Logo"
                className="h-10 sm:h-14 md:h-16 w-auto object-contain"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/180x60?text=LiteRent'; }}
              />
            </Link>

            <nav className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">Home</Link>
              <Link to="/browse" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">Browse Houses</Link>
              <Link to="/faq" className="text-[#a5c250] font-semibold border-b-2 border-[#a5c250]">FAQ</Link>
              <div className="flex items-center space-x-4">
                {(isLoggedIn || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()}>
                      <button className={`px-6 py-2.5 text-white rounded-full font-medium transition shadow-sm ${isAdminLoggedIn ? 'bg-red-600 hover:bg-red-700' : 'bg-[#294c79] hover:bg-[#1f3a5c]'}`}>
                        {getDashboardLabel()}
                      </button>
                    </Link>
                    <button onClick={handleLogout} className="px-6 py-2.5 border-2 border-red-500 text-red-600 rounded-full font-medium hover:bg-red-50 transition">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login"><button className="px-6 py-2.5 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium hover:bg-[#294c79]/10 transition">Login</button></Link>
                    <Link to="/signup"><button className="px-6 py-2.5 bg-[#a5c250] text-white rounded-full font-medium hover:bg-[#8bb83a] shadow-sm transition">Get Started</button></Link>
                  </>
                )}
              </div>
            </nav>

            <button
              className="lg:hidden p-2 rounded-md text-[#294c79] hover:bg-gray-100"
              style={{ minWidth: 44, minHeight: 44 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg className="h-6 w-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[#afc09e] py-5 px-4">
            <div className="flex flex-col space-y-1">
              {[{ to: '/', label: 'Home' }, { to: '/browse', label: 'Browse Houses' }, { to: '/faq', label: 'FAQ', active: true }].map((link) => (
                <Link key={link.to} to={link.to}
                  className={`py-3 px-2 text-base font-medium border-b border-gray-50 last:border-0 ${link.active ? 'text-[#a5c250] font-semibold' : 'text-[#294c79]'}`}
                  onClick={() => setIsMenuOpen(false)}>{link.label}</Link>
              ))}
              <div className="pt-4 flex flex-col space-y-3">
                {(isLoggedIn || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()} onClick={() => setIsMenuOpen(false)}>
                      <button className={`w-full py-3.5 text-white rounded-full font-medium ${isAdminLoggedIn ? 'bg-red-600' : 'bg-[#294c79]'}`}>{getDashboardLabel()}</button>
                    </Link>
                    <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="w-full py-3.5 border-2 border-red-500 text-red-600 rounded-full font-medium">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}><button className="w-full py-3.5 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium">Login</button></Link>
                    <Link to="/signup" onClick={() => setIsMenuOpen(false)}><button className="w-full py-3.5 bg-[#a5c250] text-white rounded-full font-medium">Get Started</button></Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ── HERO ── */}
      <section className="bg-[#294c79] text-white pt-12 sm:pt-16 pb-16 sm:pb-20 px-4 sm:px-8 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#a5c250]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-white/5 rounded-full blur-2xl pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white font-medium transition text-sm mb-8 sm:mb-10">
            ← Back to Home
          </Link>
          <span className="inline-block bg-[#a5c250]/20 border border-[#a5c250]/30 text-[#c8e87a] text-[10px] sm:text-xs font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full tracking-widest uppercase mb-4 sm:mb-5">
            Help &amp; Information
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-5 leading-tight">
            Frequently Asked Questions
          </h1>
          <p className="text-white/75 text-base sm:text-lg max-w-2xl mx-auto mb-10 sm:mb-12 leading-relaxed">
            Everything you need to know about LiteRent — from your first application and verification right through to renewal and beyond.
          </p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <span className="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-[#afc09e] text-xl pointer-events-none">🔍</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search any question…"
              className="w-full pl-12 sm:pl-14 pr-10 py-3.5 sm:py-4 rounded-2xl text-[#1a1a1a] placeholder-[#1a1a1a]/40 focus:outline-none focus:ring-4 focus:ring-[#a5c250]/50 shadow-xl text-sm sm:text-base"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')}
                className="absolute right-4 sm:right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-2xl font-light">×</button>
            )}
          </div>

          {/* Quick stats */}
          <div className="flex flex-wrap justify-center gap-5 sm:gap-8 mt-8 sm:mt-12 text-white/80 text-sm">
            <span><strong className="text-white text-base sm:text-lg font-bold">{totalQuestions}</strong> answers</span>
            <span><strong className="text-white text-base sm:text-lg font-bold">{FAQS.length}</strong> categories</span>
            <span><strong className="text-white text-base sm:text-lg font-bold">24/7</strong> self-service</span>
          </div>
        </div>
      </section>

      {/* ── VERIFICATION CALLOUT BANNER ── */}
      <div className="bg-[#e8f2d1] border-b border-[#a5c250]/40 px-4 sm:px-8 py-3.5 sm:py-4">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
          <span className="text-xl sm:text-2xl flex-shrink-0">✅</span>
          <p className="text-[#294c79] text-sm sm:text-base leading-relaxed">
            <strong>First time applying?</strong> LiteRent conducts a physical visit to verify your identity and income before approving any application.{' '}
            <button type="button" onClick={scrollToVerification}
              className="underline font-semibold hover:text-[#a5c250] transition whitespace-nowrap">
              See all Verification FAQs →
            </button>
          </p>
        </div>
      </div>

      {/* ── BODY ── */}
      <div id="faq-body" className="max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-14">

        {/* Category pills */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-8 sm:mb-10">
          <button type="button" onClick={() => setActiveCategory(null)}
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-xs sm:text-sm transition ${
              !activeCategory ? 'bg-[#294c79] text-white shadow-md' : 'bg-white border-2 border-[#afc09e] text-[#294c79] hover:border-[#a5c250]'
            }`}>
            All Topics
          </button>
          {FAQS.map(({ category, icon }) => (
            <button key={category} type="button"
              onClick={() => setActiveCategory(activeCategory === category ? null : category)}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-xs sm:text-sm transition flex items-center gap-1.5 sm:gap-2 ${
                activeCategory === category
                  ? 'bg-[#294c79] text-white shadow-md'
                  : category === 'Verification'
                  ? 'bg-[#e8f2d1] border-2 border-[#a5c250]/60 text-[#294c79] hover:border-[#a5c250]'
                  : 'bg-white border-2 border-[#afc09e] text-[#294c79] hover:border-[#a5c250]'
              }`}>
              <span>{icon}</span>
              {category}
            </button>
          ))}
        </div>

        {/* Search result banner */}
        {search.trim() && (
          <div className="bg-[#e8f2d1] border border-[#a5c250]/40 rounded-xl px-4 sm:px-5 py-3 mb-6 sm:mb-8 flex items-center gap-2">
            <span className="text-[#a5c250]">🔍</span>
            <p className="text-[#294c79] text-sm">
              <span className="font-bold">{totalResults}</span> result{totalResults !== 1 ? 's' : ''} for <span className="font-bold">&ldquo;{search}&rdquo;</span>
            </p>
            <button type="button" onClick={() => setSearch('')} className="ml-auto text-xs text-[#294c79]/60 hover:text-[#294c79] font-medium underline">Clear</button>
          </div>
        )}

        {/* No results */}
        {filtered.length === 0 && (
          <div className="bg-white p-10 sm:p-14 rounded-2xl shadow-md border-2 border-[#e8f2d1] text-center">
            <div className="text-5xl sm:text-6xl mb-5 sm:mb-6">🤔</div>
            <h3 className="text-xl sm:text-2xl font-bold text-[#294c79] mb-3">No results found</h3>
            <p className="text-[#1a1a1a]/60 mb-8 sm:mb-10 max-w-sm mx-auto text-sm sm:text-base">Try different keywords or remove the category filter.</p>
            <button type="button" onClick={() => { setSearch(''); setActiveCategory(null); }}
              className="px-8 sm:px-10 py-3.5 sm:py-4 bg-[#294c79] text-white rounded-full font-semibold hover:bg-[#1f3a5c] transition shadow-md">
              Show All Questions
            </button>
          </div>
        )}

        {/* FAQ sections */}
        {filtered.length > 0 && (
          <div className="space-y-10 sm:space-y-14">
            {filtered.map(({ category, icon, items }) => (
              <div key={category}>
                <div className="flex items-center gap-3 sm:gap-4 mb-5 sm:mb-6 pb-4 border-b-2 border-[#e8f2d1]">
                  <span className="text-2xl sm:text-3xl">{icon}</span>
                  <h2 className="text-xl sm:text-2xl font-bold text-[#294c79]">{category}</h2>
                  {category === 'Verification' && (
                    <span className="hidden sm:inline-block bg-[#a5c250]/20 border border-[#a5c250]/40 text-[#4a7a1e] text-xs font-semibold px-3 py-1 rounded-full">
                      Read this first
                    </span>
                  )}
                  <span className="ml-auto bg-[#e8f2d1] text-[#294c79] px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                    {items.length}
                  </span>
                </div>
                <div className="space-y-2 sm:space-y-3">
                  {items.map(({ q, a }) => (
                    <AccordionItem key={q} question={q} answer={a} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Still need help */}
        <div className="mt-14 sm:mt-20 bg-white rounded-2xl shadow-md border-2 border-[#e8f2d1] p-8 sm:p-10 md:p-14 text-center">
          <div className="text-5xl mb-4 sm:mb-5">💬</div>
          <h3 className="text-xl sm:text-2xl font-bold text-[#294c79] mb-3">Still have questions?</h3>
          <p className="text-[#1a1a1a]/65 mb-8 sm:mb-10 max-w-md mx-auto leading-relaxed text-sm sm:text-base">
            Our support team is available Monday – Saturday, 9 am – 5 pm WAT. We typically respond within 2 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a href="https://wa.me/2349057582412" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-3.5 sm:py-4 bg-[#25D366] text-white rounded-full font-semibold hover:bg-[#20b858] transition shadow-md text-sm sm:text-base">
              💬 Chat on WhatsApp
            </a>
            <a href="mailto:support@literent.com.ng"
              className="inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-3.5 sm:py-4 bg-[#294c79] text-white rounded-full font-semibold hover:bg-[#1f3a5c] transition shadow-md text-sm sm:text-base">
              📧 Email Us
            </a>
            <Link to="/dashboard"
              className="inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-3.5 sm:py-4 border-2 border-[#294c79] text-[#294c79] rounded-full font-semibold hover:bg-[#294c79]/5 transition text-sm sm:text-base">
              📋 Submit a Ticket
            </Link>
          </div>
        </div>

        <div className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-[#afc09e]/30 text-center text-[#1a1a1a]/60 text-xs sm:text-sm">
          © {new Date().getFullYear()} LiteRent. All rights reserved.
        </div>
      </div>
       <Footer />
    </div>
    
  );
}